import { useState } from "react";
import dayjs from "dayjs";
import { ChevronUp } from "lucide-react";

import { TableRow } from "@/components/common/table/table";
import { TruncatedText } from "@/components/common/text";
import { LoadboardToggleRow } from "@/components/dataPages/loadboard";
import { cn } from "@/utils/css";
import { formatAge } from "@/utils/date";
import { useLoadFinderStore } from "@/state/loadFinderStore";

// Format city to title case
const formatCity = (city) => {
    if (!city) return "-";
    return city
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

// Location display component matching Figma design for load finder
const LoadFinderLocation = ({ city, state }) => {
    if (!city && !state) return <span className="text-cm-p-text-secondary">-</span>;
    return (
        <div className="flex flex-col min-w-0">
            <TruncatedText className="text-sm font-medium text-cm-p-text-primary">{formatCity(city)}</TruncatedText>
            <TruncatedText className="text-xs text-cm-p-text-secondary">{state?.toUpperCase() || ""}</TruncatedText>
        </div>
    );
};

// Get CM score badge color classes based on score value
const getCmScoreColors = (score) => {
    if (score == null) return { bg: "bg-cm-p-background", text: "text-cm-p-text-primary" };
    if (score >= 85) return { bg: "bg-cm-p-container-green", text: "text-cm-p-on-container-green" };
    if (score >= 70) return { bg: "bg-cm-p-container-orange", text: "text-cm-p-on-container-orange" };
    return { bg: "bg-cm-p-container-red", text: "text-cm-p-on-container-red" };
};

// Format currency with $ prefix and thousand separators
const formatCurrency = (value) => {
    if (value == null) return null;
    return `$ ${Math.round(value).toLocaleString()}`;
};

const formatStopTime = (time) => (time ? dayjs(time, "HH:mm").format("h:mm A") : null);

const LoadFinderDate = ({ date, stop }) => {
    const earlyTime = formatStopTime(stop?.earlyTime);
    const lateTime = formatStopTime(stop?.lateTime);
    const times = [...new Set([earlyTime, lateTime].filter(Boolean))];

    return (
        <div className="flex flex-col">
            <span className="text-sm font-normal text-cm-p-text-primary truncate">{date ? dayjs(date).format("MMM D, YYYY") : "-"}</span>
            <span className="hidden xl:flex items-center gap-1 text-xs text-cm-p-text-secondary whitespace-nowrap">
                {times.length === 0 ? "-" : times[0]}
                {times.length === 2 && (
                    <>
                        <span className="w-[6px] h-px bg-cm-p-text-secondary" />
                        {times[1]}
                    </>
                )}
            </span>
        </div>
    );
};

const OfferDiffChip = ({ percentage }) => {
    if (percentage == null || Math.abs(percentage) < 1) return null;
    const isBelow = percentage < 0;
    return (
        <div
            className={cn("inline-flex items-center justify-center h-5 px-[6px] rounded-[4px] text-xs font-medium whitespace-nowrap", {
                "bg-cm-p-container-red text-cm-p-on-container-red": isBelow,
                "bg-cm-p-container-green text-cm-p-on-container-green": !isBelow,
            })}
        >
            {Math.abs(Math.round(percentage))}% {isBelow ? "below" : "above"}
        </div>
    );
};

export const TruckFilterLoadRow = ({
    item,
    index,
    withTruck = false,
    hideLoadId = false,
    cmScore,
    offerDiffPercentage,
    isNew = false,
    isLast = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const clearNewLoadHighlight = useLoadFinderStore((s) => s.clearNewLoadHighlight);

    // Extract load and route analysis from enriched item
    const load = item?.loadBoardItem ?? item;
    const routeAnalysis = item?.routeAnalysis;

    const isMultistop = (load.itineraryStops?.length ?? 0) > 2;

    const ageWhen = formatAge(load.updatedAt || load.createdAt);
    const extraStops = Math.max((load.itineraryStops?.length ?? 0) - 2, 0);
    const distanceDetails = [
        routeAnalysis?.deadheadDistance ? `${Math.round(routeAnalysis.deadheadDistance).toLocaleString()} DH` : null,
        extraStops ? `${extraStops} ${extraStops > 1 ? "stops" : "stop"}` : null,
    ].filter(Boolean);

    const scoreColors = getCmScoreColors(cmScore);
    const cellClass = "px-2 py-2 text-sm align-middle";

    return (
        <>
            <TableRow
                className={cn("hover:bg-cm-p-surface cursor-pointer h-[57px] transition-colors duration-500", {
                    "!border-t-0": index === 0,
                    "border-b border-cm-p-outline": isLast,
                    "opacity-40": load.isUnavailable,
                    "bg-cm-p-container-green": isNew,
                    "bg-transparent": !isNew,
                })}
                onClick={() => {
                    if (isNew) clearNewLoadHighlight(load.uniqueId);
                    setIsOpen(!isOpen);
                }}
            >
                {/* CM Score */}
                <td className={cellClass}>
                    <div className="flex justify-center pl-2">
                        {cmScore != null ? (
                            <div
                                className={cn(
                                    "inline-flex items-center justify-center rounded-[20px] px-2 h-[22px] min-w-[32px]",
                                    scoreColors.bg,
                                )}
                            >
                                <span className={cn("text-xs font-medium", scoreColors.text)}>{cmScore}</span>
                            </div>
                        ) : (
                            <span className="text-cm-p-text-secondary">-</span>
                        )}
                    </div>
                </td>

                {/* Truck (optional) */}
                {withTruck && (
                    <td className={cellClass}>
                        <TruncatedText className="bg-cm-p-background text-cm-p-text-primary rounded-[20px] font-medium py-[2px] px-2 text-sm inline-block max-w-full">
                            {load.truckReference?.name || ""}
                        </TruncatedText>
                    </td>
                )}

                {/* LoadID (optional) */}
                {!hideLoadId && <td className={cellClass}>{load.externalLoadId || "-"}</td>}

                {/* Pickup */}
                <td className={cellClass}>
                    <LoadFinderLocation city={load.itineraryStops?.[0]?.stopCity} state={load.itineraryStops?.[0]?.stopState} />
                </td>

                {/* Delivery */}
                <td className={cellClass}>
                    <LoadFinderLocation
                        city={load.itineraryStops?.[load.itineraryStops?.length - 1]?.stopCity}
                        state={load.itineraryStops?.[load.itineraryStops?.length - 1]?.stopState}
                    />
                </td>

                {/* Pickup date */}
                <td className={cellClass}>
                    <LoadFinderDate date={load.pickupDate} stop={load.itineraryStops?.[0]} />
                </td>

                {/* Delivery date */}
                <td className={cellClass}>
                    <LoadFinderDate date={load.deliveryDate} stop={load.itineraryStops?.[load.itineraryStops?.length - 1]} />
                </td>

                {/* Distance */}
                <td className={cellClass}>
                    <div className="flex flex-col items-end pr-2">
                        <span className="text-sm font-normal text-cm-p-text-primary whitespace-nowrap">
                            {load.loadDistance ? `${Math.round(load.loadDistance).toLocaleString()} mi` : "-"}
                        </span>
                        {distanceDetails.length > 0 && (
                            <span className="text-xs text-cm-p-text-secondary whitespace-nowrap">{distanceDetails.join(" · ")}</span>
                        )}
                    </div>
                </td>

                {/* Broker */}
                <td className={cellClass}>
                    <TruncatedText className="text-sm font-normal text-cm-p-text-primary">{load.brokerInfo?.name || "-"}</TruncatedText>
                </td>

                {/* Offer */}
                <td className={cellClass}>
                    <div className="flex flex-col items-end pr-2">
                        {load.offerRate ? (
                            <>
                                <span className="text-sm font-medium text-cm-p-text-primary whitespace-nowrap">
                                    {formatCurrency(load.offerRate)}
                                </span>
                                <OfferDiffChip percentage={offerDiffPercentage} />
                            </>
                        ) : (
                            <span className="text-cm-p-text-secondary">-</span>
                        )}
                    </div>
                </td>

                {/* CM estimate */}
                <td className={cellClass}>
                    <div className="flex flex-col items-end pr-2">
                        {!isMultistop && routeAnalysis?.estimatedPrice ? (
                            <span className="text-sm font-medium text-cm-p-text-primary whitespace-nowrap">
                                {formatCurrency(routeAnalysis.estimatedPrice)}
                            </span>
                        ) : (
                            <span className="text-cm-p-text-secondary">-</span>
                        )}
                    </div>
                </td>

                {/* Age */}
                <td className={cellClass}>
                    <div className="flex justify-end pr-2">
                        <span className="text-sm text-cm-p-text-secondary whitespace-nowrap">{ageWhen}</span>
                    </div>
                </td>

                {/* Expand arrow */}
                <td className={cellClass}>
                    <div className="flex items-center justify-center p-2">
                        <ChevronUp
                            className={cn("w-4 h-4 transition-transform text-cm-p-text-secondary", {
                                "rotate-180": !isOpen,
                            })}
                        />
                    </div>
                </td>
            </TableRow>
            <LoadboardToggleRow load={load} isOpen={isOpen} />
        </>
    );
};
